<template>
  <div class="w-full flex items-start justify-center relative h-auto mb-10 ms:pb-40">
    <div
      class="flex flex-col w-full sm:w-[95%] md:w-[95%] lg:w-[94%] xl:w-[92%] 2xl:w-[80%] container bg-gray-50 border-[1.5px] rounded-xl p-2 md:p-4 pt-2 mt-4"
    >
      <div class="flex flex-row items-center gap-2">
        <p class="text-[#061B44] font-bold text-lg md:text-xl lg:text-2xl">
          {{ $t("notifications-page") }}
        </p>
        <span
          v-if="unseen > 0"
          class="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full"
        >
          {{ unseen }}
        </span>
      </div>
      <div v-if="!isLoading && notificationList.length != 0" class="flex flex-row items-center justify-between pt-2">
        <p class="text-sm md:text-base font-medium text-slate-500">
          {{
            unseen > 1
              ? $t("youve-unseen-unread-notifications", [unseen])
              : unseen == 1
              ? $t("youve-unseen-unread-notification1_")
              : $t("youve-unseen-unread-notification")
          }}
        </p>
        <button
          v-if="unseen > 0"
          @click="markAllasseen"
          :disabled="isLoading"
          class="flex flex-row items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            clip-rule="evenodd"
            fill-rule="evenodd"
            class="h-5 w-5 fill-blue-600"
          >
            <path
              d="m9.831 16.198.002-.003-1.113-.996-.004.004-2.468-2.202c-.144-.128-.322-.191-.5-.191-.417 0-.749.337-.749.75 0 .206.084.412.249.56l2.478 2.21-.767.873-4.71-4.202c-.144-.128-.322-.191-.5-.191-.416 0-.749.337-.749.75 0 .206.084.412.25.56l5.268 4.7c.143.128.321.191.499.191.206 0 .41-.084.559-.25l1.264-1.437 1.677 1.496c.143.128.321.191.499.191.206 0 .409-.084.558-.25l11.234-12.499c.129-.143.192-.322.192-.501 0-.419-.338-.75-.748-.75-.206 0-.411.084-.559.249l-10.735 11.943zm-.451-1.75 1.114.996 8.316-9.182c.128-.143.191-.322.191-.501 0-.419-.337-.75-.748-.75-.206 0-.411.084-.559.249z"
              fill-rule="nonzero"
            />
          </svg>
          <span>{{ $t("mark-all-as-seen") || "Mark all as seen" }}</span>
        </button>
      </div>
      <!-- notification card -->
      <div class="flex flex-col w-full items-center justify-start">
        <!-- Skeleton Loaders -->
        <div
          v-if="isLoading"
          class="w-full"
        >
          <div
            v-for="n in 6"
            :key="`skeleton-${n}`"
            class="flex w-full items-center py-4"
          >
            <div class="container flex flex-row items-center p-2 rounded-lg w-full">
              <!-- Icon skeleton -->
              <div class="h-10 w-10 md:h-14 md:w-14 bg-gray-200 animate-pulse rounded-lg"></div>
              <!-- Content skeleton -->
              <div class="flex flex-col pl-4 w-full space-y-2">
                <div class="h-4 md:h-5 bg-gray-200 animate-pulse rounded w-3/4"></div>
                <div class="h-3 md:h-4 bg-gray-200 animate-pulse rounded w-full"></div>
                <div class="h-3 bg-gray-200 animate-pulse rounded w-1/2"></div>
              </div>
              <!-- Button skeleton -->
              <div class="h-10 w-[16%] lg:w-[20%] bg-gray-200 animate-pulse rounded-md ml-2"></div>
            </div>
          </div>
        </div>
        <li
      v-else-if="notificationList.length > 0"
      v-for="notification in notificationList"
      :key="notification.id"
      class="flex w-full items-center py-4"
    >
      <div
        @click="handleClickEvent(notification)"
        :class="{
          'bg-blue-50': !notification.seen,
          'bg-white': notification.seen,
          'border-l-4 border-blue-500': !notification.seen && locale !== 'ar',
          'border-r-4 border-blue-500': !notification.seen && locale === 'ar',
          'opacity-60 cursor-not-allowed pointer-events-none': notification.type === 'ONETIME_AUDIO_UPLOAD' && (notification.consumed || notification.uploadAlreadyUsed),
        }"
        class="container flex flex-row items-center p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 hover:shadow-lg hover:scale-[1.01] cursor-pointer relative"
      >
        <!-- Loading overlay -->
        <div
          v-if="isNavigating[notification.id]"
          class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg"
        >
          <svg
            class="animate-spin h-6 w-6 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <div
          :class="{
            'bg-green-100':
              notification.type == 'VIDEO' ||
              notification.type == 'EXTVIDEO' ||
              notification.type == 'ONETIME_VIDEO_RESULT',
            'bg-red-100': notification.type == 'YOUTUBE' || notification.type == 'ONETIME_YOUTUBE_RESULT',
            'bg-blue-100':
              notification.type == 'AUDIO' ||
              notification.type == 'EXTAUDIO' ||
              notification.type == 'ONETIME_AUDIO_UPLOAD' ||
              notification.type == 'ONETIME_AUDIO_RESULT',
            'bg-amber-100': isOneTimeNotification(notification) && notification.type !== 'ONETIME_YOUTUBE_RESULT' && notification.type !== 'ONETIME_AUDIO_UPLOAD' && notification.type !== 'ONETIME_VIDEO_RESULT' && notification.type !== 'ONETIME_AUDIO_RESULT',
          }"
          class="flex items-center justify-center p-1 h-10 w-10 md:h-14 md:w-14 rounded-lg"
        >
          <svg
            v-if="
              notification.type == 'AUDIO' ||
              notification.type == 'EXTAUDIO' ||
              notification.type == 'ONETIME_AUDIO_RESULT'
            "
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-[#6193F6] h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path
              d="M1 13h-1v-1h1v1zm22-1h-1v1h1v-1zm-20-1h-1v3h1v-3zm18 0h-1v3h1v-3zm-14 0h-1v3h1v-3zm10-1h-1v5h1v-5zm-12 0h-1v5h1v-5zm14-1h-1v7h1v-7zm-10 0h-1v7h1v-7zm2-2h-1v10h1v-10zm4 0h-1v10h1v-10zm-2-2h-1v14h1v-14z"
            />
          </svg>
          <svg
            v-else-if="notification.type == 'ONETIME_AUDIO_UPLOAD'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-[#6193F6] h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path d="M12 16V4l3 3 1.5-1.5L12 1 7.5 5.5 9 7l3-3v12zm-8 4h16v-2H4v2z" />
          </svg>
          <svg
            v-else-if="isOneTimeNotification(notification) && notification.type !== 'ONETIME_YOUTUBE_RESULT' && notification.type !== 'ONETIME_AUDIO_UPLOAD' && notification.type !== 'ONETIME_VIDEO_RESULT' && notification.type !== 'ONETIME_AUDIO_RESULT'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-amber-600 h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path
              d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"
            />
          </svg>
          <svg
            v-if="
              notification.type == 'VIDEO' ||
              notification.type == 'EXTVIDEO' ||
              notification.type == 'ONETIME_VIDEO_RESULT'
            "
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-green-500 h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path
              d="M15 3c1.104 0 2 .896 2 2v4l7-4v14l-7-4v4c0 1.104-.896 2-2 2h-13c-1.104 0-2-.896-2-2v-14c0-1.104.896-2 2-2h13zm0 17c.552 0 1-.448 1-1v-14c0-.551-.448-1-1-1h-13c-.551 0-1 .449-1 1v14c0 .552.449 1 1 1h13zm2-9.848v3.696l6 3.429v-10.554l-6 3.429z"
            />
          </svg>
          <svg
            v-if="notification.type == 'YOUTUBE' || notification.type == 'ONETIME_YOUTUBE_RESULT'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-red-500 h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path
              d="M10 9.333l5.333 2.662-5.333 2.672v-5.334zm14-4.333v14c0 2.761-2.238 5-5 5h-14c-2.761 0-5-2.239-5-5v-14c0-2.761 2.239-5 5-5h14c2.762 0 5 2.239 5 5zm-4 7c-.02-4.123-.323-5.7-2.923-5.877-2.403-.164-7.754-.163-10.153 0-2.598.177-2.904 1.747-2.924 5.877.02 4.123.323 5.7 2.923 5.877 2.399.163 7.75.164 10.153 0 2.598-.177 2.904-1.747 2.924-5.877z"
            />
          </svg>
          <svg
            v-if="notification.type == 'TRANSLATION'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-[#6193F6] h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path
              d="M24 24h-2.784l-1.07-3h-4.875l-1.077 3h-2.697l4.941-13h2.604l4.958 13zm-4.573-5.069l-1.705-4.903-1.712 4.903h3.417zm-9.252-12.804c.126-.486.201-.852.271-1.212l-2.199-.428c-.036.185-.102.533-.22 1-.742-.109-1.532-.122-2.332-.041.019-.537.052-1.063.098-1.569h2.456v-2.083h-2.161c.106-.531.198-.849.288-1.149l-2.147-.645c-.158.526-.29 1.042-.422 1.794h-2.451v2.083h2.184c-.058.673-.093 1.371-.103 2.077-2.413.886-3.437 2.575-3.437 4.107 0 1.809 1.427 3.399 3.684 3.194 2.802-.255 4.673-2.371 5.77-4.974 1.134.654 1.608 1.753 1.181 2.771-.396.941-1.561 1.838-3.785 1.792v2.242c2.469.038 4.898-.899 5.85-3.166.93-2.214-.132-4.635-2.525-5.793zm-2.892 1.531c-.349.774-.809 1.544-1.395 2.15-.09-.646-.151-1.353-.184-2.108.533-.07 1.072-.083 1.579-.042zm-3.788.724c.062.947.169 1.818.317 2.596-1.996.365-2.076-1.603-.317-2.596zm11.236-1.745l-2.075-5.533 5.414-1.104-.976 1.868c2.999 2.418 4.116 5.645 4.532 8.132-1.736-2.913-3.826-4.478-5.885-5.321l-1.01 1.958zm-7.895 10.781l1.985 5.566-5.43 1.016 1.006-1.852c-2.96-2.465-4.021-5.654-4.397-8.148 1.689 2.94 3.749 4.483 5.794 5.36l1.042-1.942zm10.795-6.029"
            />
          </svg>
          <svg
            v-if="notification.type == 'MESSAGE'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="fill-[#6193F6] h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
          >
            <path
              d="M12 1c-6.338 0-12 4.226-12 10.007 0 2.05.739 4.063 2.047 5.625.055 1.83-1.023 4.456-1.993 6.368 2.602-.47 6.301-1.508 7.978-2.536 9.236 2.247 15.968-3.405 15.968-9.457 0-5.812-5.701-10.007-12-10.007zm1 15h-2v-6h2v6zm-1-7.75c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"
            />
          </svg>
        </div>
        <div class="flex flex-col pl-4 w-full overflow-hidden">
          <p :class="{
            'text-lg font-semibold': !notification.seen,
            'text-lg font-medium': notification.seen,
          }" class="text-slate-800">
            {{ getTitle(notification) }}
          </p>
          <p 
          :class="{
          'text-green-500':
            notification.status != NotificationStatus.FAILED,
          'text-red-500': notification.status === NotificationStatus.FAILED,
        }"
          class="text-sm font-medium break-words whitespace-normal">
            {{ getDescription(notification) }}
          </p>
                  <p class="text-xs text-slate-500 font-normal mt-1">
                    {{ formatDate(notification.createdAt) }} {{ formatTime(notification.createdAt) }}
                  </p>
        </div>
        <!-- Mark as seen button -->
        <button
          v-if="!notification.seen"
          @click.stop="handleMarkAsSeen(notification)"
          :disabled="isMarkingSeen[notification.id]"
          :class="{
            'mr-2': locale !== 'ar',
            'ml-2': locale === 'ar',
          }"
          class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
          :title="$t('mark-all-as-seen') || 'Mark as seen'"
        >
          <svg
            v-if="markedNotifications.has(notification.id)"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-green-500 animate-checkmark"
          >
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
          <svg
            v-else-if="isMarkingSeen[notification.id]"
            class="animate-spin h-5 w-5 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-slate-600"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
      </div>
    </li>
        <div
          v-else
          class="w-full flex flex-col my-auto items-center justify-center py-40"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-slate-400 mb-6"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          <p class="font-semibold text-base text-slate-600 text-center w-[60%]">
            {{
              $t(
                "youre-all-caught-up-there-are-currently-no-new-notifications-for-you-keep-an-eye-out-for-any-updates-in-the-future"
              )
            }}
          </p>
        </div>
      </div>
      <!-- navigation card -->
      <div 
      v-if="notificationList.length != 0"
      class="flex flex-row items-center justify-between w-full mt-2">
        <button
          @click="getPreviousPage"
          type="button"
          :disabled="currentPage <= 1"
          class="bg-white text-slate-900 border-slate-600 border rounded-md px-2 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <p class="font-normal text-base">{{ $t("previous") }}</p>
        </button>
        <p class="text-sm md:text-base text-black font-normal px-2">
          {{ $t("page") }} {{ currentPage }}{{ $t("of") }} {{ totalPages }}
        </p>
        <button
          @click="getNextPage"
          type="button"
          :disabled="currentPage >= totalPages"
          class="bg-white text-slate-900 border-slate-600 border rounded-md px-2 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <p class="font-normal text-base">{{ $t("next") }}</p>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes checkmark {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-checkmark {
  animation: checkmark 0.3s ease-in-out;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { API_BASE_URL, GETNOTIFICATION, MARKALLSEEN } from "../utils/appConst";
import { useAuthStore } from "../store/auth";
import { NotificationModel, NotificationStatus } from "../utils/notificationModel";
import { useNotificationStore } from "../store/notification";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Language, languages } from "../utils/languageList";
import { toast } from "vue3-toastify";

const router = useRouter();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const notificationList = ref<NotificationModel[]>([]);
const isLoading = ref(true);
const hasError = ref(false);
const getNotificationUrl = `${API_BASE_URL}${GETNOTIFICATION}`;
const markAllSeenUrl = `${API_BASE_URL}${MARKALLSEEN}`;
const currentPage = ref(1);
const totalPages = ref(0);
const { t, locale } = useI18n();
const unseen = computed(() => authStore.unseenCount);
const isNavigating = ref<Record<string, boolean>>({});
const isMarkingSeen = ref<Record<string, boolean>>({});
const markedNotifications = ref<Set<string>>(new Set());

onMounted(async () => {
  await getNotification();
});

const getNextPage = async () => {
  try {
    if (currentPage.value >= totalPages.value) {
      return;
    }
    currentPage.value++;
    await getNotification();
  } catch (error) {
    console.log("getNextPage: ", error);
  }
};
/* todo */
const getPreviousPage = async () => {
  try {
    if (currentPage.value <= 1) {
      return;
    }
    currentPage.value--;
    await getNotification();
  } catch (error) {
    console.log("getNextPage: ", error);
  }
};

const getNotification = async () => {
  try {
    isLoading.value = true;
    const getData = await fetch(`${getNotificationUrl}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ page: currentPage.value }),
    });

    if (!getData.ok) {
      throw new Error(`HTTP error! Status: ${getData.status}`);
    }

    const { notifications, totalpages } = await getData.json();

    if (totalpages) {
      totalPages.value = totalpages;
    }

    console.log("[Notifications] API response page=", currentPage.value, "totalpages=", totalpages, "count=", notifications?.length ?? 0);
    (notifications || []).forEach((n: any, i: number) => {
      console.log("[Notifications] raw[" + i + "]", {
        notificationId: n.notificationId,
        type: n.type,
        message: n.message ?? "(none)",
        originalLang: n.originalLang ?? "(none)",
        videoTitle: n.videoTitle ?? "(none)",
        audioTitle: n.audioTitle ?? "(none)",
      });
    });

    notificationList.value = notifications.map((notification: any) => {
      const mapped = {
        id: notification.notificationId,
        message: notification.message || null,
        type: notification.type,
        seen: notification.seen,
        createdAt: new Date(notification.createdDate),
        originalLang: notification.originalLang,
        targetLang: notification.targetLang,
        requestId: notification.requestId || null,
        relatedRequestId: notification.relatedRequestId || null,
        videoLink: notification.youtubeVideoId || notification.videoLink || null,
        audioLink: notification.audioLink || null,
        fileName: notification.videoTitle || notification.audioTitle || null,
        status:
          (notification.status as NotificationStatus) ||
          NotificationStatus.SUCCESS,
        uploadAlreadyUsed: notification.uploadAlreadyUsed === true,
        consumed: notification.consumed === true,
        orderSourceType: notification.orderSourceType ?? null,
      };
      console.log("[Notifications] mapped", { id: mapped.id, type: mapped.type, message: mapped.message ?? "(none)", originalLang: mapped.originalLang ?? "(none)", fileName: mapped.fileName ?? "(none)" });
      return mapped;
    });

    console.log("[Notifications] screen opened, all notifications:", notificationList.value);
    isLoading.value = false;
  } catch (error) {
    console.log("getNotification: ", error);
    isLoading.value = false;
    hasError.value = true;
  }
};

const markAsSeen = async (notifyId: string) => {
  try {
    if (isLoading.value || isMarkingSeen.value[notifyId]) {
      return;
    }
    isMarkingSeen.value[notifyId] = true;

    const getData = await fetch(`${getNotificationUrl}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ notificationId: notifyId }),
    });

    if (!getData.ok) {
      throw new Error(`HTTP error! Status: ${getData.status}`);
    }

    const result = await getData.json();

    if (result.message) {
      const notificationIndex = notificationList.value.findIndex(
        (notification) => notification.id === notifyId
      );

      if (notificationIndex !== -1) {
        notificationList.value[notificationIndex].seen = true;
        // Show checkmark animation
        markedNotifications.value.add(notifyId);
        setTimeout(() => {
          markedNotifications.value.delete(notifyId);
        }, 2000);
      }
      authStore.decrementSeen();
    }

    isMarkingSeen.value[notifyId] = false;
  } catch (error) {
    console.log("markAsSeen: ", error);
    isMarkingSeen.value[notifyId] = false;
    hasError.value = true;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const handleMarkAsSeen = async (notification: NotificationModel) => {
  await markAsSeen(notification.id);
};

const handleClickEvent = async (notification: NotificationModel) => {
  try {
    if (isNavigating.value[notification.id] || notification.status === NotificationStatus.FAILED) {
      return;
    }
    if (notification.type === "ONETIME_AUDIO_UPLOAD" && (notification.consumed || notification.uploadAlreadyUsed)) {
      return;
    }
    
    if (!notification.seen) {
      await markAsSeen(notification.id);
    }
    
    if (notification.requestId) {
      isNavigating.value[notification.id] = true;
      notificationStore.setRequest(notification.requestId);
      
      if (notification.type === "AUDIO") {
        router.push({
          name: "AudioTranscriptor",
          query: {
            req: notification.requestId,
          },
        });
      } else if (notification.type === "EXTAUDIO") {
        router.push({
          name: "AudioTranscriptor",
          query: {
            req: notification.requestId,
            link: notification.audioLink,
          },
        });
      } else if (notification.type === "YOUTUBE") {
        router.push({
          name: "Youtube",
          query: {
            req: notification.requestId,
          },
        });
      } else if (notification.type === "VIDEO") {
        router.push({
          name: "VideoEditor",
          query: {
            req: notification.requestId,
          },
        });
      } else if (notification.type === "EXTVIDEO") {
        router.push({
          name: "VideoEditor",
          query: {
            req: notification.requestId,
            link: notification.videoLink,
          },
        });
      } else if (notification.type === "TRANSLATION") {
        router.push({
          name: "AudioTranscriptor",
          query: {
            text: notification.requestId,
          },
        });
      } else if (notification.type === "ONETIME_AUDIO_UPLOAD") {
        const sourceType = notification.orderSourceType ?? "audio";
        if (sourceType === "youtube") {
          router.push({
            name: "Youtube",
            query: {
              onetime: "success",
              orderId: notification.requestId,
              type: "youtube",
            },
          });
        } else if (sourceType === "video") {
          router.push({
            name: "VideoEditor",
            query: {
              onetime: "success",
              orderId: notification.requestId,
              type: "video",
            },
          });
        } else {
          router.push({
            name: "AudioTranscriptor",
            query: {
              onetime_order: notification.requestId,
              type: "onetime_upload",
            },
          });
        }
      } else if (notification.type === "ONETIME_YOUTUBE_RESULT") {
        router.push({
          name: "Youtube",
          query: {
            onetime_order: notification.requestId,
            type: "onetime_result",
          },
        });
      } else if (notification.type === "ONETIME_VIDEO_RESULT") {
        router.push({
          name: "VideoEditor",
          query: {
            onetime_order: notification.requestId,
            type: "onetime_result",
          },
        });
      } else if (notification.type === "ONETIME_AUDIO_RESULT") {
        router.push({
          name: "AudioTranscriptor",
          query: {
            onetime_order: notification.requestId,
            type: "onetime_result",
          },
        });
      }
    }
  } catch (error) {
    console.log(`handleClickEvent : ${error}`);
    isNavigating.value[notification.id] = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const markAllasseen = async () => {
  try {
    if (isLoading.value || authStore.unseenCount === 0) {
      return;
    }
    isLoading.value = true;

    const getData = await fetch(markAllSeenUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });

    if (!getData.ok) {
      throw new Error(`HTTP error! Status: ${getData.status}`);
    }

    const result = await getData.json();

    if (result.message) {
      // Mark all as seen in local list
      notificationList.value.forEach((notification) => {
        notification.seen = true;
      });
      await authStore.getUnseenCount();
      
      toast(t("mark-all-as-seen") || "All notifications marked as seen", {
        autoClose: 2000,
        type: "success",
        theme: "light",
      });
    }

    isLoading.value = false;
  } catch (error) {
    console.log("markAllasseen: ", error);
    isLoading.value = false;
    hasError.value = true;
    toast(t('were-fixing-a-technical-issue-so-please-try') || "An error occurred. Please try again later.", {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
  }
};

const getLanguageName = (code: string) => {
  return languages.find((language: Language) => language.langCode === code)
    ?.langName || code;
};

function isOneTimeNotification(notification: NotificationModel): boolean {
  const type = String(notification.type || "").toUpperCase();
  return type === "ONETIME_AUDIO_UPLOAD" || type === "ONETIME_AUDIO_RESULT" || type.includes("ONETIME");
}

function getTitle(notification: NotificationModel): string {
  if (isOneTimeNotification(notification)) {
    if (notification.type === "ONETIME_AUDIO_UPLOAD" || String(notification.type).toUpperCase().includes("UPLOAD")) {
      return notification.orderSourceType === "video" ? t("One-time_video_upload_title") : t("One-time_audio_upload_title");
    }
    if (notification.type === "ONETIME_YOUTUBE_RESULT") return t("One-time_youtube_result_title");
    if (notification.type === "ONETIME_VIDEO_RESULT") return t("One-time_video_result_title");
    return t("One-time_audio_result_title");
  }
  if (notification.type == "TRANSLATION") {
    return t("Your_Translation_is_Ready");
  } else {
    if (notification.status === NotificationStatus.FAILED) {
      return t("transcription-failed");
    }
    return t("Your_Transcription_is_Ready");
  }
}

function getDescription(notification: NotificationModel): string {
  if (isOneTimeNotification(notification)) {
    let base =
      notification.message
        ? notification.message
        : String(notification.type).toUpperCase().includes("UPLOAD")
          ? (notification.orderSourceType === "video" ? t("One-time_video_upload_description") : t("One-time_audio_upload_description"))
          : notification.type === "ONETIME_YOUTUBE_RESULT"
            ? t("One-time_youtube_result_description")
            : notification.type === "ONETIME_VIDEO_RESULT"
              ? t("One-time_video_result_description")
              : t("One-time_audio_result_description");
    const hasTarget = notification.targetLang != null && String(notification.targetLang).trim() !== "";
    const isUpload = String(notification.type).toUpperCase().includes("UPLOAD");
    // Upload: no language part – just the base message for a cleaner feel
    if (isUpload) {
      return base;
    }
    const hasTitle = notification.fileName != null && String(notification.fileName).trim() !== "";
    const hasSource = notification.originalLang != null && String(notification.originalLang).trim() !== "" && String(notification.originalLang).trim().toLowerCase() !== "auto";
    if (hasTitle || hasSource || hasTarget) {
      let legacy = hasTitle ? notification.fileName!.trim() + " " : "";
      if (hasSource && hasTarget) {
        legacy += (hasTitle ? "" : t("Your") + " ") + t("transcription") + " " + t("from") + " " + getLanguageName(notification.originalLang!.trim()) + " " + t("to") + " " + getLanguageName(String(notification.targetLang!).trim()) + " " + t("is-now-available");
      } else if (hasSource) {
        legacy += (hasTitle ? "" : t("Your") + " ") + t("transcription") + " " + t("from") + " " + getLanguageName(notification.originalLang!.trim()) + " " + t("is-now-available");
        if (hasTarget) legacy += " " + t("to") + " " + getLanguageName(String(notification.targetLang!).trim());
      } else {
        legacy += base;
        if (hasTarget) legacy += " " + t("to") + " " + getLanguageName(String(notification.targetLang!).trim());
      }
      return legacy.trim();
    }
    return base;
  }
  if (notification.status === NotificationStatus.FAILED) {
    return t(
      "there-was-an-issue-processing-your-transcription-please-try-again-later"
    );
  }
  const hasSource = notification.originalLang != null && String(notification.originalLang).trim() !== "";
  if (!hasSource) {
    return notification.type === "TRANSLATION"
      ? t("Your") + " " + t("translation") + " " + t("is-now-available")
      : t("Your") + " " + t("transcription") + " " + t("is-now-available");
  }
  let message = notification.fileName || t("Your");
  message +=
    notification.type === "TRANSLATION"
      ? " " + t("translation")
      : " " + t("transcription");
  message +=
    " " + t("from") + " " + getLanguageName(notification.originalLang!);
  if (notification.type == "TRANSLATION") {
    return (message +=
      " " +
      t("to") +
      " " +
      notification.targetLang +
      " " +
      t("is-now-available"));
  } else {
    return (message += " " + t("is-now-available"));
  }
}

const formatDate = (date: Date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };
  return date.toLocaleDateString(locale.value === "ar" ? "ar-SA" : "en-US", options);
};

const formatTime = (date: Date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleTimeString(locale.value === "ar" ? "ar-SA" : "en-US", timeOptions);
};


</script>
